<template>
  <AIAssistantActions
    :class="{ 'tw:p-4': !card, 'tw:p-2': card }"
    :copy-text="copyText"
  >
    <AISummaryStreamFeedback
      v-if="stream"
      :ai-feature-configuration-id="aiFeatureConfigurationId"
      :ai-input="feedbackInput"
      :ai-result="feedbackResult"
      data-testid="feedback"
      :source="feedbackSource"
      :stream-id="streamId"
      @feedback-recorded="onFeedbackRecorded"
    />
  </AIAssistantActions>
</template>

<script setup lang="ts">
import { computed } from "vue";

import AIAssistantActions from "shared/components/AI/AIAssistant/AIAssistantActions.vue";
import { type AIFeedbackRecordedEvent } from "shared/composables/useAIFeedback";
import useAIStore from "shared/stores/ai";
import { type AISummary } from "shared/stores/ai";
import useStreamsStore from "shared/stores/streams";
import { type Nullable, type Stream } from "shared/types";

import AISummaryStreamFeedback from "./AISummaryStreamFeedback.vue";

interface AISummaryStreamResponseProps {
  card?: boolean;
  streamId: number;
}

const props = defineProps<AISummaryStreamResponseProps>();

const aiStore = useAIStore();
const { getAISummary, setAISummary } = aiStore;

const streamsStore = useStreamsStore();
const { getStreamById } = streamsStore;

const stream = computed<Nullable<Stream>>(
  () => getStreamById(props.streamId) || null
);

const feedbackSource = computed<string>(() => {
  if (stream.value) return `${stream.value.label} - Mention Stream`;

  return "";
});

const aiStreamSummary = computed<Required<AISummary>>(() =>
  getAISummary(props.streamId)
);

const feedbackInput = computed<Record<string, unknown>>(() => ({
  after: aiStreamSummary.value.dateRange.after,
  before: aiStreamSummary.value.dateRange.before,
  streamId: props.streamId,
}));

const feedbackResult = computed<Record<string, unknown>>(
  () => aiStreamSummary.value.result || {}
);

const aiFeatureConfigurationId = computed<number | undefined>(
  () => aiStreamSummary.value.result?.aiFeatureConfigurationId
);

const copyText = computed<string>(
  () =>
    aiStreamSummary.value.result?.topics
      .map((topic) => `${topic.title}\n${topic.summary}`)
      .join("\n\n") || ""
);

function onFeedbackRecorded(feedback: AIFeedbackRecordedEvent) {
  setAISummary(props.streamId, feedback);
}
</script>
